import React, { useCallback, useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import { TextField, DefaultButton } from '@fluentui/react';
import classNames from 'classnames';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { notificationApi } from '@bingads-webui-react/notification-center';
import { getStepStyles } from '@bingads-webui-campaign-react/conversion-wizard-components';

import { MAX_EMAIL_ADDRESSES_COUNT, splitEmailAddresses, genEmailAddresses, canSendEmail } from '../../utils/email';
import { postEmail } from '../../../conversion-goals-repository';
import { getEventTagModel } from '../../utils/tag-generator';

const UpdatedEventType = {
  UET: 'UetTagInstructionsEmailV2',
  Event: 'UetTagAndEventTagInstructionsEmailV2',
};

const TagSetupSendToDeveloperViewUnstyled = (props) => {
  const {
    classes,
    i18n,
    scope,
    serverContext,
    logActivity,
    tagId,
    goalContext,
    setIsValid,
    permissions,
  } = props;

  const [emailAddressesTextInput, setEmailAddressesTextInput] = useState('');
  const [errorText, setErrorText] = useState(null);
  const [isFirstRendered, setIsFirstRendered] = useState(true);
  const [emailStatus, setEmailStatus] = useState(null);

  const isEnhancedConvPilotOn = useMemo(() =>
    permissions && permissions.dynamic && permissions.dynamic.EnhancedConversions, [permissions]);

  const genErrorMessage = useCallback((validEmailAddresses, invalidEmailAddresses) => {
    if (isFirstRendered) {
      setIsFirstRendered(false);
      return (null);
    }
    const exceedMaxValidMessage = i18n.getString(_TL_(('You have reached the maximum of {0} email addresses. Remove at least 1 email address to send the message.'))).split('{0}');

    return (
      <div className="show-error">
        {validEmailAddresses.length > MAX_EMAIL_ADDRESSES_COUNT &&
          <div className="exceed-max-valid-address">
            {`${exceedMaxValidMessage[0]}${MAX_EMAIL_ADDRESSES_COUNT}${exceedMaxValidMessage[1]}`}
          </div>
        }
        {validEmailAddresses.length === 0 &&
          <div className="no-valid-email-address">
            {i18n.getString(_TL_('Please enter at least one valid email address.'))}
          </div>
        }
        {invalidEmailAddresses.length > 0 &&
          <div className="invalid-email-address">
            <div className="title">
              {i18n.getString(_TL_('Please correct these invalid email addresses:'))}
            </div>
            {_.map(invalidEmailAddresses, (address, idx) => (
              <li key={idx} className={`invalid-${idx}`}>
                {address}
              </li>
            ))}
          </div>
        }
      </div>
    );
  }, [i18n, isFirstRendered]);

  const onChangeEmailAddress = useCallback((textString) => {
    const userInputs = textString.split('\n');
    const [validEmailAddresses, invalidEmailAddresses] = splitEmailAddresses(userInputs);
    setErrorText(genErrorMessage(validEmailAddresses, invalidEmailAddresses));
    setEmailAddressesTextInput(userInputs);
  }, [genErrorMessage]);

  const { aid, cid } = scope.values || {};
  const { Token } = serverContext;
  const { Type, GoalValueType, IsEnhancedConversionsEnabled } = goalContext || {};
  const eventType = Type === 'EventGoal' || Type === 'ProductConversionGoal' || GoalValueType === 'VariantValue' ? 'Event' : 'UET';
  const updatedEventType = isEnhancedConvPilotOn && IsEnhancedConversionsEnabled ? UpdatedEventType[eventType] : eventType;
  const emailContent = useMemo(() => (eventType === 'Event' ?
    Object.assign({
      TagId: tagId.toString(),
    }, getEventTagModel(goalContext)) :
    {
      TagId: tagId.toString(),
    }), [eventType, goalContext, tagId]);

  const onClickSendEmails = useCallback(() => {
    const [validEmailAddresses, invalidEmailAddresses] = splitEmailAddresses(emailAddressesTextInput);
    if (canSendEmail(validEmailAddresses, invalidEmailAddresses)) {
      setIsValid(true);
      const emailPromise = postEmail(cid, aid, Token, updatedEventType, genEmailAddresses(validEmailAddresses), emailContent);
      logActivity.trace(`Sending tag to ${validEmailAddresses.length} emails`);
      if (emailPromise) {
        setEmailStatus('pending');
      } else {
        logActivity.trace('Failed to send tag to email');
        setEmailStatus('failed');
      }

      emailPromise.then(() => {
        setEmailStatus('success');
      }).catch(() => {
        logActivity.trace('Failed to send tag to email');
        setEmailStatus('failed');
      });
    } else {
      setIsValid(false);
    }
  }, [emailAddressesTextInput, setIsValid, cid, aid, Token, updatedEventType, emailContent, logActivity]);

  useEffect(() => {
    let notificationId;
    if (emailStatus === 'success') {
      notificationId = notificationApi.showInfo(i18n.getString(_TL_('Email sent')));
    } else if (emailStatus === 'failed') {
      notificationId = notificationApi.showWarning(i18n.getString(_TL_('Oh, no! We weren\'t able to send the email message. Please try again.')));
    }

    return () => notificationId && notificationApi.dismiss(notificationId);
  }, [emailStatus, i18n]);


  return (
    <div className={classes.contentBlock}>
      <div>
        {i18n.getString(_TL_('Enter the email address or addresses of the people who will edit your website’s code and we’ll send them your tracking tag (you’ll be included on the mail).'))}
      </div>

      <div className={classNames(classes.section, classes.sendEmailsTextbox)}>
        <TextField
          label={i18n.getString(_TL_('Send email to'))}
          multiline
          rows={3}
          placeholder={i18n.getString(_TL_('Enter one per line'))}
          validateOnFocusOut
          defaultValue={emailAddressesTextInput}
          onGetErrorMessage={onChangeEmailAddress}
          ariaLabel={i18n.getString(_TL_('Enter the email address or addresses of the people who will edit your website’s code and we’ll send them your tracking tag (you’ll be included on the mail).'))}
          aria-required="true"
        />
      </div>

      <div className={classes.section}>
        <DefaultButton text={i18n.getString(_TL_('Send'))} onClick={onClickSendEmails} />
      </div>
      <div className={classes.errorText}>
        {errorText}
      </div>
    </div>
  );
};

TagSetupSendToDeveloperViewUnstyled.propTypes = {
  classes: PropTypes.shape(_.mapObject(
    getStepStyles(),
    _.constant(PropTypes.string.isRequired)
  )).isRequired,
  i18n: PropTypes.shape({}).isRequired,
  scope: PropTypes.shape({}).isRequired,
  serverContext: PropTypes.shape({
    Token: PropTypes.string.isRequired,
  }).isRequired,
  logActivity: PropTypes.shape({}).isRequired,
  tagId: PropTypes.number.isRequired,
  goalContext: PropTypes.objectOf(PropTypes.any),
  setIsValid: PropTypes.func,
  permissions: PropTypes.shape({
    dynamic: PropTypes.shape({
      EnhancedConversions: PropTypes.bool,
    }),
  }),
};

TagSetupSendToDeveloperViewUnstyled.defaultProps = {
  goalContext: null,
  setIsValid: _.identity,
  permissions: {
    dynamic: {
      EnhancedConversions: false,
    },
  },
};

export const TagSetupSendToDeveloperView = withDefaultStyles(TagSetupSendToDeveloperViewUnstyled, getStepStyles);
